import cookieHelper from '../Services/Restore/library/helpers/cookieHelper';

/**
 * Canonical role keys - aligned with backend service_roles.role_key.
 * Use these for all permission checks so frontend matches backend.
 */
export const ROLE_KEYS = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  COORDINATOR: 'coordinator',
  COACH: 'coach',
  PROVIDER: 'provider',
  PATIENT: 'patient',
};

/**
 * Normalize any role string to canonical role_key (matches backend service_roles.role_key).
 * Handles "Super Administrator", "super admin", "SuperAdmin" etc. -> "superadmin"
 */
export const getNormalizedRole = (role) => {
  if (!role || typeof role !== "string") return "";
  const trimmed = role.trim().toLowerCase();
  const collapsed = trimmed.replace(/\s+/g, "");
  if (collapsed === "superadmin" || collapsed === "superadministrator" || collapsed === "supeadmin") return ROLE_KEYS.SUPERADMIN;
  if (collapsed === "admin") return ROLE_KEYS.ADMIN;
  if (collapsed === "coordinator") return ROLE_KEYS.COORDINATOR;
  if (collapsed === "coach") return ROLE_KEYS.COACH;
  if (collapsed === "provider") return ROLE_KEYS.PROVIDER;
  if (collapsed === "patient") return ROLE_KEYS.PATIENT;
  return trimmed;
};

/**
 * Get canonical role_key from API data. Prefers role_key when present (backend alignment).
 * @param {string|{ role_key?: string, role_name?: string }} roleOrObj - role string or object with role_key/role_name
 * @returns {string} - canonical role_key
 */
export const getRoleKey = (roleOrObj) => {
  if (roleOrObj == null) return "";
  if (typeof roleOrObj === "string") return getNormalizedRole(roleOrObj);
  const key = roleOrObj.role_key || roleOrObj.roleKey;
  if (key && typeof key === "string") return getNormalizedRole(key);
  const name = roleOrObj.role_name || roleOrObj.roleName;
  return getNormalizedRole(name || "");
};

/**
 * Check if a user role is admin or superadmin
 * @param {string|null|undefined} role - The role to check (can be from getHighestRole or direct role string)
 * @returns {boolean} - True if role is admin or superadmin
 */
export const isAdminOrSuperAdmin = (role) => {
  if (!role) return false;
  const key = getRoleKey(role);
  return key === ROLE_KEYS.ADMIN || key === ROLE_KEYS.SUPERADMIN;
};

/**
 * Check if a staff has admin or superadmin among their roles (single role, compound role string, and/or roles array).
 * Use this when staff can have multiple roles (e.g. "Admin, Coach") and the action should be
 * allowed if admin/superadmin is one of the assigned roles.
 * @param {string|null|undefined} role - Single or compound role string (e.g. "Admin, Coach" from staff.role)
 * @param {Array|null|undefined} rolesArray - Optional roles array (e.g. staff.roles with objects like { role_name } or strings)
 * @returns {boolean} - True if admin or superadmin is present in any of the roles
 */
export const hasAdminOrSuperAdminAmongRoles = (role, rolesArray = null) => {
  const keys = [];
  // Split compound strings like "Admin, Coach" before normalising
  if (role != null && typeof role === 'string' && role.trim()) {
    role.split(',').forEach((r) => {
      const key = getRoleKey(r.trim());
      if (key) keys.push(key);
    });
  }
  if (rolesArray && Array.isArray(rolesArray)) {
    rolesArray.forEach((r) => {
      const key = getRoleKey(typeof r === 'string' ? r : r);
      if (key) keys.push(key);
    });
  }
  return keys.some((k) => k === ROLE_KEYS.ADMIN || k === ROLE_KEYS.SUPERADMIN);
};

/**
 * Check if a staff has superadmin among their roles (single role, compound role string, and/or roles array).
 * Use this for platform-level surfaces (e.g. SA Console) that must not be visible to regular admins.
 * @param {string|null|undefined} role - Single or compound role string (e.g. "Admin, Coach" from staff.role)
 * @param {Array|null|undefined} rolesArray - Optional roles array (e.g. staff.roles with objects like { role_name } or strings)
 * @returns {boolean} - True if superadmin is present in any of the roles
 */
export const hasSuperAdminAmongRoles = (role, rolesArray = null) => {
  const keys = [];
  if (role != null && typeof role === 'string' && role.trim()) {
    role.split(',').forEach((r) => {
      const key = getRoleKey(r.trim());
      if (key) keys.push(key);
    });
  }
  if (rolesArray && Array.isArray(rolesArray)) {
    rolesArray.forEach((r) => {
      const key = getRoleKey(typeof r === 'string' ? r : r);
      if (key) keys.push(key);
    });
  }
  return keys.some((k) => k === ROLE_KEYS.SUPERADMIN);
};

/**
 * Check if a staff has 'Coach' among their roles (single role, compound role string, and/or roles array).
 * Use this when staff can have multiple roles (e.g. "Admin, Coach") and an action/feature that's
 * normally Coach-only should still apply because Coach is one of the assigned roles — even if the
 * staff also holds Admin/SuperAdmin.
 * @param {string|null|undefined} role - Single or compound role string (e.g. "Admin, Coach" from staff.role)
 * @param {Array|null|undefined} rolesArray - Optional roles array (e.g. staff.roles with objects like { role_name } or strings)
 * @returns {boolean} - True if Coach is present in any of the roles
 */
export const hasCoachAmongRoles = (role, rolesArray = null) => {
  const keys = [];
  if (role != null && typeof role === 'string' && role.trim()) {
    role.split(',').forEach((r) => {
      const key = getRoleKey(r.trim());
      if (key) keys.push(key);
    });
  }
  if (rolesArray && Array.isArray(rolesArray)) {
    rolesArray.forEach((r) => {
      const key = getRoleKey(typeof r === 'string' ? r : r);
      if (key) keys.push(key);
    });
  }
  return keys.some((k) => k === ROLE_KEYS.COACH);
};

/**
 * Check if a user role matches any of the provided roles (by role_key).
 * Use getRoleKey so "Super Administrator" / role_key "superadmin" both match.
 */
export const hasRole = (role, rolesToCheck) => {
  if (!role || !Array.isArray(rolesToCheck) || rolesToCheck.length === 0) return false;
  const key = getRoleKey(role);
  return rolesToCheck.some((r) => getRoleKey(r) === key);
};

/**
 * Check if role can view all active users (admin, superadmin, coordinator)
 */
export const canViewAllActiveUsers = (role) => {
  return hasRole(role, [ROLE_KEYS.ADMIN, ROLE_KEYS.SUPERADMIN, ROLE_KEYS.COORDINATOR]);
};

/**
 * Check if role is a coach
 */
export const isCoach = (role) => {
  return hasRole(role, [ROLE_KEYS.COACH]);
};

/**
 * Check if role is coordinator
 */
export const isCoordinator = (role) => {
  return hasRole(role, [ROLE_KEYS.COORDINATOR]);
};

/**
 * Check if role is provider
 */
export const isProvider = (role) => {
  return hasRole(role, [ROLE_KEYS.PROVIDER]);
};

/**
 * Check if role can view all signups (admin, superadmin, coordinator, provider)
 */
export const canViewAllSignups = (role) => {
  return hasRole(role, [ROLE_KEYS.ADMIN, ROLE_KEYS.SUPERADMIN, ROLE_KEYS.COORDINATOR, ROLE_KEYS.PROVIDER]);
};

/**
 * Check if the currently logged-in user is an Admin or Super Admin.
 * Checks cookieHelper userDetails, localStorage userServiceAccess, and direct roles.
 * @returns {boolean}
 */
export const isCurrentUserAdminOrSuperAdmin = () => {
  try {
    const userDetails = cookieHelper.getUserDetails() || {};
    const userServiceAccess = JSON.parse(localStorage.getItem('userServiceAccess') || '{}');

    // Check compound roles via hasAdminOrSuperAdminAmongRoles
    if (hasAdminOrSuperAdminAmongRoles(userDetails?.role, userServiceAccess?.roles)) {
      return true;
    }

    // Check userDetails fields (role, user_type, role_name, etc.)
    const directRole = userDetails?.role || userDetails?.user_type || userDetails?.userType || userDetails?.role_name || userDetails?.roleName || userDetails?.role_key || userDetails?.roleKey;
    if (directRole) {
      const r = String(directRole).toLowerCase().trim().replace(/[\s_]+/g, '');
      if (r === 'admin' || r === 'superadmin' || r === 'superadministrator' || r === 'sleepadmin') {
        return true;
      }
    }

    // Check userServiceAccess roles array
    if (Array.isArray(userServiceAccess.roles)) {
      const match = userServiceAccess.roles.some((role) => {
        const name = String(role?.role_name || role?.role_key || role?.role || role || '').toLowerCase().trim().replace(/[\s_]+/g, '');
        return name === 'admin' || name === 'superadmin' || name === 'superadministrator' || name === 'sleepadmin';
      });
      if (match) return true;
    }

    // Check direct localStorage user_role or role
    const storedRole = localStorage.getItem('user_role') || localStorage.getItem('role');
    if (storedRole) {
      const s = String(storedRole).toLowerCase().trim().replace(/[\s_]+/g, '');
      if (s === 'admin' || s === 'superadmin' || s === 'superadministrator' || s === 'sleepadmin') {
        return true;
      }
    }

    return false;
  } catch (e) {
    return false;
  }
};